import { StatusModal } from '@/components/StatusModal';
import { useEscapeKey } from '@/hooks/useEscapeKey';
import { ApiError, apiFetch } from '@/lib/apiFetch';
import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';

type Radek = {
  code: string;
  name: string;
  image: string;
  label: string;
  sku: string;
  spotreba: number;
  availability: string;
  in_production: number;
  sold_90d: number;
  sold_365d: number;
  demand: number;
  price: number;
  price_vat: number;
  value_czk: number;
  /** Zapnutý režim „na zakázku" — hladina ve dnech, jinak null. */
  active_level: number | null;
};

type Latka = {
  latka_id: string;
  name: string;
  rolls: number;
  meters_stock: number;
  meters_planned: number;
  meters_promised: number;
  meters_reserve: number;
  meters_incoming: number;
  incoming_eta: number | null;
  /** Dodání látky je až po termínu, který bychom zákazníkovi slíbili. */
  incoming_late: number | null;
  meters_free: number;
  pieces_possible: number;
  missing_spotreba: number;
  missing_second: number;
  items: Radek[];
};

type Config = { enabled: boolean; level: number };

type Data = {
  config: Config;
  s_cestou: boolean;
  summary: {
    fabrics: number;
    /** Nabídnuto ze všech velikostí, které nabídnout jdou. */
    offered: number;
    offerable: number;
    pieces: number;
    value_czk: number;
  };
  fabrics: Latka[];
};

/** 12345 → „12 345 Kč". */
function fmtCzk(n: number): string {
  return `${Math.round(n).toLocaleString('cs-CZ')} Kč`;
}

/** Unix ms → „12.8.2026". */
function fmtDate(ms: number): string {
  const d = new Date(ms);
  return `${d.getDate()}.${d.getMonth() + 1}.${d.getFullYear()}`;
}

/** Metry s jedním desetinným místem. */
function fmtM(n: number): string {
  return `${(Math.round(n * 10) / 10).toString().replace('.', ',')} m`;
}

/**
 * Šití na zakázku — fáze 1. Nic nepřepíná, jen ukazuje, kolik vyprodaných
 * velikostí bychom uměli ušít z látky, která leží na dílně, a co by to
 * přineslo. Podle toho se pak rozhodne, jestli režim zapnout naostro.
 */
export function NaZakazkuPage() {
  const [data, setData] = useState<Data | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<string | null>(null);
  const [toast, setToast] = useState<{ msg: string; kind: 'success' | 'error' } | null>(null);

  // Výchozí = jako automat (bezLatky), který látku na cestě počítá vždycky —
  // jinak přehled hlásil „došla" u látky, na které automat dál nabízí.
  const [sCestou, setSCestou] = useState(true);
  const [confirmOff, setConfirmOff] = useState(false);
  const [prefixInput, setPrefixInput] = useState('');
  const [confirmPrefix, setConfirmPrefix] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setData(await apiFetch<Data>(`/api/vyroba/na-zakazku${sCestou ? '?s_cestou=1' : ''}`));
    } catch (e: unknown) {
      setErr(e instanceof ApiError ? e.describe() : 'Načtení selhalo');
    }
  }, [sCestou]);

  useEffect(() => {
    load();
  }, [load]);

  async function nastavit(zmena: Partial<Config>) {
    try {
      const r = await apiFetch<{ zhasnuto?: number }>('/api/vyroba/na-zakazku/nastaveni', {
        method: 'POST',
        body: zmena,
      });
      if (r.zhasnuto) {
        setToast({ msg: `Režim vypnut, zhasnuto ${r.zhasnuto} velikostí`, kind: 'success' });
      }
      await load();
    } catch (e: unknown) {
      setToast({ msg: e instanceof ApiError ? e.describe() : 'Uložení selhalo', kind: 'error' });
    }
  }

  /** Hromadně vypne „na zakázku" u všech produktů, jejichž kód začíná prefixem. */
  async function vypnoutPrefix(prefix: string) {
    setConfirmPrefix(null);
    try {
      const r = await apiFetch<{ zhasnuto: number }>('/api/vyroba/na-zakazku/vypnout-prefix', {
        method: 'POST',
        body: { prefix },
      });
      setToast({
        msg: `Vypnuto ${r.zhasnuto} velikostí s kódem začínajícím na „${prefix}"`,
        kind: 'success',
      });
      setPrefixInput('');
      await load();
    } catch (e: unknown) {
      setToast({ msg: e instanceof ApiError ? e.describe() : 'Vypnutí selhalo', kind: 'error' });
    }
  }

  /** Nabídne naráz všechny dosud nenabídnuté velikosti z jedné látky. */
  async function nabidnoutVse(f: Latka) {
    const cekaji = f.items.filter((i) => !i.active_level);
    if (cekaji.length === 0) return;
    setBusy(`latka-${f.latka_id}`);
    let ok = 0;
    let chyba = '';
    for (const i of cekaji) {
      try {
        await apiFetch('/api/vyroba/na-zakazku/prepnout', {
          method: 'POST',
          body: { sku: i.sku, product_code: i.code, variant_name: i.label, on: true },
        });
        ok++;
      } catch (e: unknown) {
        // Jedna velikost neprošla → zbytek dokončíme, důvod ukážeme na konci.
        if (!chyba) chyba = e instanceof ApiError ? e.describe() : 'neznámá chyba';
      }
    }
    setBusy(null);
    setToast(
      ok === cekaji.length
        ? { msg: `Nabídnuto ${ok} velikostí z látky ${f.name}`, kind: 'success' }
        : { msg: `Nabídnuto ${ok} z ${cekaji.length}. Důvod: ${chyba}`, kind: 'error' },
    );
    await load();
  }

  async function prepnout(i: Radek, on: boolean) {
    setBusy(i.sku);
    try {
      const r = await apiFetch<{ level?: number }>('/api/vyroba/na-zakazku/prepnout', {
        method: 'POST',
        body: { sku: i.sku, product_code: i.code, variant_name: i.label, on },
      });
      // Bez hlášky a bez přenačítání — stav řádku přepíšeme rovnou, ať to
      // při zapínání víc velikostí za sebou neseká.
      setData((prev) =>
        prev
          ? {
              ...prev,
              fabrics: prev.fabrics.map((f) => ({
                ...f,
                items: f.items.map((x) =>
                  x.sku === i.sku
                    ? { ...x, active_level: on ? (r.level ?? prev.config.level) : null }
                    : x,
                ),
              })),
            }
          : prev,
      );
    } catch (e: unknown) {
      setToast({ msg: e instanceof ApiError ? e.describe() : 'Přepnutí selhalo', kind: 'error' });
    } finally {
      setBusy(null);
    }
  }

  const chybiSpotreba = (data?.fabrics ?? []).reduce((s, f) => s + f.missing_spotreba, 0);

  return (
    <>
      <div className="mb-6 flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-2xl font-bold text-text">Šití na zakázku</h1>
          <p className="text-sm text-text-secondary mt-0.5 max-w-3xl">
            Vyprodané velikosti, které bychom uměli ušít z látky ležící na dílně.
          </p>
        </div>
        <Link
          to="/vyroba/striharna/na-zakazku/log"
          title="Co se kdy zaplo a vyplo — a proč"
          className="text-sm font-semibold text-text-secondary border border-border rounded-lg px-4 py-2.5 hover:bg-surface-hover transition-colors shrink-0"
        >
          Log
        </Link>
      </div>

      {err && (
        <div className="text-sm text-danger bg-danger-light border border-danger/30 rounded-lg px-4 py-3 mb-4">
          {err}
        </div>
      )}
      {!err && data === null && <p className="text-sm text-text-muted">Počítám…</p>}

      {data && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-6">
            <Box label="Hodnota příležitosti (30 dní)" value={fmtCzk(data.summary.value_czk)} big />
            <Box
              label="Nabídnuto k ušití na zakázku"
              value={`${data.summary.offered} / ${data.summary.offerable}`}
              hint={
                data.summary.offered < data.summary.offerable
                  ? `Ještě ${data.summary.offerable - data.summary.offered} velikostí nenabízíme`
                  : 'Nabízíme všechno, co jde'
              }
            />
          </div>

          <div className="bg-surface rounded-xl shadow-card px-5 py-4 mb-6 flex flex-wrap items-center gap-x-6 gap-y-3">
            {data.config.enabled ? (
              <button
                type="button"
                onClick={() => setConfirmOff(true)}
                title="Zhasne všechny nabízené velikosti a zakáže u nich prodej do mínusu"
                className="bg-danger hover:opacity-90 text-white text-base font-bold rounded-lg px-6 py-3 cursor-pointer transition-opacity"
              >
                Vypnout režim
              </button>
            ) : (
              <button
                type="button"
                onClick={() => nastavit({ enabled: true })}
                className="bg-action hover:bg-action-dark text-white text-base font-bold rounded-lg px-6 py-3 cursor-pointer transition-colors"
              >
                Zapnout režim
              </button>
            )}
            <span className="text-sm text-text-secondary">
              {data.config.enabled ? (
                <>
                  Nabízí se{' '}
                  <strong className="text-text">
                    {data.fabrics.reduce(
                      (n, f) => n + f.items.filter((i) => i.active_level).length,
                      0,
                    )}
                  </strong>{' '}
                  velikostí
                </>
              ) : (
                'Režim je vypnutý — nic se nenabízí'
              )}
            </span>
            <div className="flex items-center gap-2 text-sm text-text">
              <span className="font-semibold">Slib:</span>
              {[7, 10, 14].map((d) => (
                <button
                  key={d}
                  type="button"
                  onClick={() => nastavit({ level: d })}
                  className={`rounded-md px-3 py-1.5 text-sm font-semibold cursor-pointer transition-colors ${
                    data.config.level === d
                      ? 'bg-action text-white'
                      : 'bg-bg border border-border text-text-secondary hover:border-text-muted'
                  }`}
                >
                  do {d} dní
                </button>
              ))}
            </div>
            <label className="flex items-center gap-2 text-sm text-text-secondary cursor-pointer">
              <input
                type="checkbox"
                checked={sCestou}
                onChange={(e) => setSCestou(e.target.checked)}
              />
              Počítat i látku na cestě
            </label>
            {data.config.enabled && (
              <div className="flex items-center gap-2 text-sm text-text ml-auto">
                <span className="font-semibold">Hromadně vypnout kód:</span>
                <input
                  type="text"
                  value={prefixInput}
                  onChange={(e) => setPrefixInput(e.target.value.toUpperCase())}
                  placeholder="např. K"
                  maxLength={20}
                  className="w-20 border border-border rounded-md px-2 py-1.5 text-sm font-mono uppercase focus:outline-none focus:ring-2 focus:ring-primary/30"
                />
                <button
                  type="button"
                  onClick={() => setConfirmPrefix(prefixInput.trim())}
                  disabled={!prefixInput.trim()}
                  className="bg-danger hover:opacity-90 text-white text-sm font-semibold rounded-md px-3 py-1.5 cursor-pointer transition-opacity disabled:opacity-40 disabled:cursor-not-allowed"
                >
                  Vypnout
                </button>
              </div>
            )}
          </div>

          {chybiSpotreba > 0 && (
            <div className="text-sm text-text bg-warning-light border-l-4 border-warning rounded-r-lg px-4 py-3 mb-6">
              U <strong>{chybiSpotreba}</strong> velikostí chybí spotřeba látky na kus — do výpočtu
              se nepočítají. Doplň ji ve Zdroje → Kategorie produktů.
            </div>
          )}

          {data.fabrics.length === 0 ? (
            <p className="text-sm text-text-muted">
              Na dílně není žádná přijatá role látky, ze které bychom mohli šít.
            </p>
          ) : (
            <div className="space-y-5">
              {data.fabrics.map((f) => (
                <div key={f.latka_id} className="bg-surface rounded-xl shadow-card overflow-hidden">
                  <div className="px-5 py-4 border-b border-border flex flex-wrap items-baseline justify-between gap-x-6 gap-y-1">
                    <h2 className="text-lg font-bold text-text">{f.name}</h2>
                    <div className="text-[15px] text-text-secondary">
                      sklad <strong className="text-text">{fmtM(f.meters_stock)}</strong> ({f.rolls}{' '}
                      rolí)
                      {f.meters_incoming > 0 && (
                        <>
                          {' '}
                          · 🚚 na cestě{' '}
                          <strong className={sCestou ? 'text-text' : 'text-text-muted'}>
                            {fmtM(f.meters_incoming)}
                          </strong>
                          {f.incoming_eta ? ` (${fmtDate(f.incoming_eta)})` : ''}
                        </>
                      )}{' '}
                      · čeká na střihárnu {fmtM(f.meters_planned)} · rezerva{' '}
                      {fmtM(f.meters_reserve)} · volné{' '}
                      <strong className="text-action-dark">{fmtM(f.meters_free)}</strong> ≈{' '}
                      <strong className="text-action-dark">{f.pieces_possible} ks</strong>
                    </div>
                    {f.incoming_late && (
                      <p className="w-full text-sm font-semibold text-warning bg-warning-light border border-warning/40 rounded-lg px-3 py-2">
                        ⚠️ Nabídka stojí na látce, která dorazí až {fmtDate(f.incoming_late)} — po
                        termínu, který dnešní objednávce slíbíme (do {data.config.level} dní). Ušít
                        včas to nemusíme stihnout.
                      </p>
                    )}
                  </div>

                  {f.meters_free <= 0 && f.items.some((i) => !i.active_level) && (
                    <p className="mx-5 mt-3 text-sm font-semibold text-warning bg-warning-light border border-warning/40 rounded-lg px-3 py-2">
                      {sCestou
                        ? 'Volná látka došla — metry drží kusy čekající na střihárnu, objednávky a rezerva. Nabídnout nejde, automat by ji za pár minut sám vypnul.'
                        : 'Bez látky na cestě volná látka došla. Automat ale látku na cestě počítá — zaškrtni „Počítat i látku na cestě" pro jeho pohled.'}
                    </p>
                  )}

                  {data.config.enabled &&
                    f.meters_free > 0 &&
                    f.items.some((i) => !i.active_level) && (
                      <div className="px-5 pt-3">
                        <button
                          type="button"
                          onClick={() => nabidnoutVse(f)}
                          disabled={busy === `latka-${f.latka_id}`}
                          className="rounded-md bg-action hover:bg-action-dark disabled:opacity-50 px-4 py-2 text-sm font-bold text-white cursor-pointer transition-colors"
                        >
                          {busy === `latka-${f.latka_id}`
                            ? 'Nabízím…'
                            : `Nabídnout vše (${f.items.filter((i) => !i.active_level).length})`}
                        </button>
                      </div>
                    )}
                  {f.missing_second > 0 && (
                    <p className="px-5 pt-3 text-xs text-text-muted">
                      {f.missing_second} produktů z téhle látky se nenabízí — chybí jejich druhá
                      (kontrastní) látka.
                    </p>
                  )}
                  {f.items.length === 0 ? (
                    <p className="px-5 py-4 text-sm text-text-muted">
                      Z téhle látky teď není co nabídnout — žádná velikost není vyprodaná.
                    </p>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm">
                        <thead>
                          <tr className="bg-bg text-left text-xs font-semibold text-text-secondary">
                            <th className="px-4 py-2.5">Produkt</th>
                            <th className="px-4 py-2.5 w-24">Velikost</th>
                            <th className="px-4 py-2.5 w-28 text-right">Prodáno 90 dní</th>
                            <th className="px-4 py-2.5 w-28 text-right">Odhad / 30 dní</th>
                            <th className="px-4 py-2.5 w-24 text-right">Spotřeba</th>
                            <th className="px-4 py-2.5 w-28 text-right">Cena/ks bez DPH</th>
                            <th className="px-4 py-2.5 w-28 text-right">Potenciál</th>
                            <th className="px-4 py-2.5 w-36" />
                          </tr>
                        </thead>
                        <tbody>
                          {f.items.map((i) => (
                            <tr key={i.sku} className="border-b border-border-light">
                              <td className="px-4 py-2">
                                <div className="flex items-center gap-2.5">
                                  {i.image ? (
                                    <img
                                      src={i.image}
                                      alt=""
                                      loading="lazy"
                                      className="w-9 h-9 rounded object-contain border border-border bg-white shrink-0"
                                    />
                                  ) : (
                                    <div className="w-9 h-9 rounded border border-border bg-bg shrink-0" />
                                  )}
                                  <span className="min-w-0">
                                    <span className="block font-semibold text-text truncate">
                                      {i.name}
                                    </span>
                                    <span className="block text-xs font-mono text-text-muted">
                                      {i.sku}
                                    </span>
                                  </span>
                                </div>
                              </td>
                              <td className="px-4 py-2 text-text">
                                {i.label}
                                {i.in_production > 0 && (
                                  <span
                                    className="block text-xs text-text-muted"
                                    title={i.availability}
                                  >
                                    {i.in_production} ks ve výrobě (prodané)
                                  </span>
                                )}
                              </td>
                              <td className="px-4 py-2 text-right text-text-secondary">
                                {i.sold_90d} ks
                              </td>
                              <td
                                className="px-4 py-2 text-right text-text-muted"
                                title={`Za rok se prodalo ${i.sold_365d} ks této velikosti`}
                              >
                                {i.demand} ks
                              </td>
                              <td className="px-4 py-2 text-right text-text-secondary">
                                {fmtM(i.spotreba)}
                              </td>
                              <td
                                className="px-4 py-2 text-right text-text-secondary"
                                title={`V e-shopu ${fmtCzk(i.price_vat)} s DPH`}
                              >
                                {fmtCzk(i.price)}
                              </td>
                              <td className="px-4 py-2 text-right font-semibold text-action-dark">
                                {fmtCzk(i.value_czk)}
                              </td>
                              <td className="px-4 py-2 text-right">
                                <button
                                  type="button"
                                  onClick={() => prepnout(i, !i.active_level)}
                                  disabled={
                                    busy === i.sku ||
                                    (!i.active_level &&
                                      (!data.config.enabled || f.meters_free <= 0))
                                  }
                                  title={
                                    !data.config.enabled
                                      ? 'Nejdřív zapni režim nahoře'
                                      : !i.active_level && f.meters_free <= 0
                                        ? 'Na látce nezbývají volné metry'
                                        : 'Přepne dostupnost varianty v e-shopu'
                                  }
                                  className={`rounded-md px-3 py-1.5 text-sm font-semibold cursor-pointer transition-colors disabled:opacity-40 ${
                                    i.active_level
                                      ? 'bg-action-light text-action-dark border border-action/40'
                                      : 'bg-bg border border-border text-text-secondary hover:border-text-muted'
                                  }`}
                                >
                                  {busy === i.sku
                                    ? '…'
                                    : i.active_level
                                      ? `✓ do ${i.active_level} dní`
                                      : 'Nabídnout'}
                                </button>
                              </td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </div>
              ))}
            </div>
          )}

          <p className="text-xs text-text-muted mt-6 max-w-3xl">
            Jak se to počítá: volné metry = sklad − látka na kusy, které ještě čekají na střihárnu
            (co má zakázku, je nastříhané a z role odečtené) − 10 % rezerva; metry se mezi velikosti
            nerozdělují, u látky je jen strop, kolik kusů celkem unese. Nabízí se každá velikost,
            kterou si zákazník nemůže koupit — i ta, ze které se něco šije, protože ty kusy už mají
            zákazníka. Odhad poptávky je na 30 dní: vychází ze skutečného prodeje, a u velikostí,
            které dlouho chybí (a proto se prodat nemohly), podíl té velikosti z ročního prodeje
            celého produktu. Cena je z e-shopu, přepočtená bez DPH.
          </p>
        </>
      )}

      {confirmOff && data && (
        <VypnoutModal
          pocet={data.fabrics.reduce((n, f) => n + f.items.filter((i) => i.active_level).length, 0)}
          onCancel={() => setConfirmOff(false)}
          onConfirm={async () => {
            setConfirmOff(false);
            await nastavit({ enabled: false });
          }}
        />
      )}

      {confirmPrefix !== null && confirmPrefix !== '' && (
        <VypnoutPrefixModal
          prefix={confirmPrefix}
          onCancel={() => setConfirmPrefix(null)}
          onConfirm={() => vypnoutPrefix(confirmPrefix)}
        />
      )}

      {toast && (
        <StatusModal message={toast.msg} kind={toast.kind} onClose={() => setToast(null)} />
      )}
    </>
  );
}

/** Vypnutí celého režimu — sáhne do e-shopu u všech nabízených velikostí. */
function VypnoutModal({
  pocet,
  onCancel,
  onConfirm,
}: {
  pocet: number;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  useEscapeKey(onCancel);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Zavřít"
        onClick={onCancel}
        className="fixed inset-0 bg-black/60 cursor-default"
      />
      <div className="relative w-full max-w-md bg-surface rounded-2xl shadow-modal p-6 reveal">
        <h2 className="text-lg font-bold text-text">Vypnout šití na zakázku?</h2>
        <p className="text-sm text-text-secondary mt-2">
          U <strong className="text-text">{pocet}</strong> velikostí se v e-shopu vrátí původní
          dostupnost a zakáže se prodej do mínusu. Zákazníci si je od té chvíle nekoupí.
        </p>
        <p className="text-sm text-text-secondary mt-2">
          Objednávky, které už přišly, platí dál — ty je potřeba doušít.
        </p>
        <p className="text-sm text-text-muted mt-2">
          Každá velikost se zapisuje do e-shopu zvlášť, takže to pár vteřin potrvá. V e-shopu se
          změna projeví hned, čísla o skladovosti tady se dorovnají do 15 minut.
        </p>
        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="text-sm font-semibold text-text-secondary border border-border rounded-lg px-4 py-2.5 hover:bg-surface-hover cursor-pointer transition-colors"
          >
            Zrušit
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="bg-danger text-white text-sm font-bold rounded-lg px-5 py-2.5 hover:opacity-90 cursor-pointer transition-opacity"
          >
            Vypnout režim
          </button>
        </div>
      </div>
    </div>
  );
}

/** Hromadné vypnutí podle prefixu kódu produktu — jen tyhle produkty, ne celý režim. */
function VypnoutPrefixModal({
  prefix,
  onCancel,
  onConfirm,
}: {
  prefix: string;
  onCancel: () => void;
  onConfirm: () => void;
}) {
  useEscapeKey(onCancel);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center p-4">
      <button
        type="button"
        aria-label="Zavřít"
        onClick={onCancel}
        className="fixed inset-0 bg-black/60 cursor-default"
      />
      <div className="relative w-full max-w-md bg-surface rounded-2xl shadow-modal p-6 reveal">
        <h2 className="text-lg font-bold text-text">Vypnout kódy začínající na „{prefix}"?</h2>
        <p className="text-sm text-text-secondary mt-2">
          U všech aktuálně nabízených velikostí, jejichž kód produktu začíná na{' '}
          <strong className="text-text font-mono">{prefix}</strong>, se v e-shopu vrátí původní
          dostupnost a zakáže se prodej do mínusu. Ostatní produkty zůstanou beze změny.
        </p>
        <p className="text-sm text-text-secondary mt-2">
          Objednávky, které už přišly, platí dál — ty je potřeba doušít.
        </p>
        <div className="mt-6 flex justify-end gap-2">
          <button
            type="button"
            onClick={onCancel}
            className="text-sm font-semibold text-text-secondary border border-border rounded-lg px-4 py-2.5 hover:bg-surface-hover cursor-pointer transition-colors"
          >
            Zrušit
          </button>
          <button
            type="button"
            onClick={onConfirm}
            className="bg-danger text-white text-sm font-bold rounded-lg px-5 py-2.5 hover:opacity-90 cursor-pointer transition-opacity"
          >
            Vypnout „{prefix}*"
          </button>
        </div>
      </div>
    </div>
  );
}

function Box({
  label,
  value,
  big,
  hint,
}: { label: string; value: string; big?: boolean; hint?: string }) {
  return (
    <div className="bg-surface rounded-xl shadow-card px-5 py-4">
      <div className="text-xs text-text-muted uppercase tracking-wider">{label}</div>
      <div className={`font-bold text-2xl ${big ? 'text-action-dark' : 'text-text'}`}>{value}</div>
      {hint && <div className="text-sm text-text-secondary mt-0.5">{hint}</div>}
    </div>
  );
}
